import { RotateCcw, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useDemo } from '@/contexts/useDemo';

/**
 * The persistent "this is a demo" bar.
 *
 * In normal document flow rather than fixed: every page renders its own
 * `Header` with `sticky top-0`, and a fixed banner would mean retuning each of
 * those offsets. It scrolls away, which is the trade-off, and the cheaper one.
 */
export function DemoBanner() {
  const { isDemoMode, person, role, switchRole, resetDemo, exitDemo } = useDemo();
  const navigate = useNavigate();
  if (!isDemoMode || !person) return null;

  /**
   * Secondary on purpose. The demo's default experience is one role with a
   * simulated counterpart on the other side — switching is for a reviewer who
   * wants to look at the driver's screens, not the way the flow is meant to
   * be experienced.
   */
  const other = role === 'driver' ? 'rider' : 'driver';
  const seeOtherSide = () => {
    switchRole(other);
    navigate(other === 'driver' ? '/driver-dashboard' : '/rides');
  };

  return (
    <div
      className="relative z-50 bg-primary/10 border-b border-primary/20"
      role="region"
      aria-label="Demo mode"
    >
      <div className="container mx-auto px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-medium">Demo</span>
        <span className="text-muted-foreground">
          You are <strong className="text-foreground">{person.name}</strong>, a {role}. Sample
          data — nothing here is saved.
        </span>

        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={seeOtherSide}>
            See the {other}&rsquo;s side
          </Button>
          <Button variant="ghost" size="sm" onClick={resetDemo} className="gap-1.5">
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </Button>
          <Button variant="ghost" size="sm" onClick={exitDemo} aria-label="Exit demo">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
